/**
 * OrderTripAssignmentCard — assign an order to an existing trip from the order detail view.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import { LocalShipping, Launch, LinkOff, OpenInNew, Add } from "@mui/icons-material";
import { format } from "date-fns";
import {
  addOrderToTrip,
  getTrip,
  getTrips,
  removeOrderFromTrip,
} from "../../trips/services/tripService.js";
import { logError } from "@/shared/utils/logger.js";
import { openAppPopup } from "@/shared/utils/openAppPopup.js";
import {
  corridorsMatch,
  formatCorridorDisplay,
} from "@/shared/utils/routeCorridorUtils.js";
import { ACTIVE_TRIP_STATUSES } from "../../trips/constants/tripStatuses.js";

const ACTIVE_TRIP_STATUS_SET = new Set(ACTIVE_TRIP_STATUSES);

const formatTripStatus = (status) =>
  String(status || "unknown").replaceAll("_", " ").toUpperCase();

const formatDeparture = (value) => {
  if (!value) return "TBD";
  try {
    return format(new Date(value), "PPp");
  } catch {
    return String(value);
  }
};

/**
 * @param {Object} props
 * @param {Object} props.order
 * @param {function} [props.onAssignmentChange] — called after assign/remove with updated tripId
 */
const OrderTripAssignmentCard = ({ order, onAssignmentChange }) => {
  const orderId = order?.id;
  const currentTripId = order?.tripId || null;

  const [modalOpen, setModalOpen] = useState(false);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [assigningTripId, setAssigningTripId] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);
  const [trips, setTrips] = useState([]);
  const [linkedTrip, setLinkedTrip] = useState(null);
  const [matchRouteOnly, setMatchRouteOnly] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadLinkedTrip = async () => {
      if (!currentTripId) {
        setLinkedTrip(null);
        return;
      }
      try {
        const trip = await getTrip(currentTripId);
        if (!cancelled) {
          setLinkedTrip(trip || null);
        }
      } catch (err) {
        logError("OrderTripAssignmentCard: failed to load linked trip", {
          tripId: currentTripId,
          error: err?.message,
        });
        if (!cancelled) {
          setLinkedTrip(null);
        }
      }
    };
    loadLinkedTrip();
    return () => {
      cancelled = true;
    };
  }, [currentTripId]);

  const orderRouteLabel = formatCorridorDisplay(order?.routeCorridor) || order?.routeCorridor;

  const handleCreateTripPopup = () => {
    const params = new URLSearchParams({ create: "1" });
    if (orderId) {
      params.set("assignOrderId", orderId);
    }
    if (orderRouteLabel) {
      params.set("assignRoute", orderRouteLabel);
    }
    openAppPopup(`/trips?${params.toString()}`, {
      title: "Create Trip",
      width: 900,
      height: 860,
    });
  };

  const fetchTrips = useCallback(async () => {
    if (!orderId) return;
    setLoadingTrips(true);
    setError(null);
    try {
      const response = await getTrips({ statusIn: ACTIVE_TRIP_STATUSES }, 1, 50);
      const list = response?.data?.trips || [];
      setTrips(list);
    } catch (err) {
      logError("OrderTripAssignmentCard: failed to load trips", {
        orderId,
        error: err?.message,
      });
      setError(err?.message || "Failed to load trips");
      setTrips([]);
    } finally {
      setLoadingTrips(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (modalOpen) {
      fetchTrips();
    }
  }, [modalOpen, fetchTrips]);

  useEffect(() => {
    if (!modalOpen) return undefined;

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "trip-created") return;

      fetchTrips();

      if (event.data.orderIds?.includes(orderId) && event.data.tripId) {
        setModalOpen(false);
        onAssignmentChange?.({ tripId: event.data.tripId });
      }
    };

    const handleFocus = () => {
      fetchTrips();
    };

    window.addEventListener("message", handleMessage);
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("focus", handleFocus);
    };
  }, [modalOpen, fetchTrips, orderId, onAssignmentChange]);

  const visibleTrips = useMemo(() => {
    return trips.filter((trip) => {
      if (trip.id === currentTripId) return false;
      if (!ACTIVE_TRIP_STATUS_SET.has(trip.status)) return false;
      if (matchRouteOnly && order?.routeCorridor) {
        return corridorsMatch(order.routeCorridor, trip.routeCorridor);
      }
      return true;
    });
  }, [trips, currentTripId, matchRouteOnly, order?.routeCorridor]);

  const handleAssign = async (tripId) => {
    if (!orderId || !tripId || tripId === currentTripId) return;
    setAssigningTripId(tripId);
    setError(null);
    try {
      if (currentTripId) {
        await removeOrderFromTrip(currentTripId, orderId);
      }
      await addOrderToTrip(tripId, orderId);
      const trip = await getTrip(tripId);
      setLinkedTrip(trip || null);
      setModalOpen(false);
      onAssignmentChange?.({ tripId });
    } catch (err) {
      setError(err?.message || "Failed to assign order to trip");
    } finally {
      setAssigningTripId(null);
    }
  };

  const handleRemove = async () => {
    if (!orderId || !currentTripId) return;
    setRemoving(true);
    setError(null);
    try {
      await removeOrderFromTrip(currentTripId, orderId);
      setLinkedTrip(null);
      onAssignmentChange?.({ tripId: null });
    } catch (err) {
      setError(err?.message || "Failed to remove order from trip");
    } finally {
      setRemoving(false);
    }
  };

  const currentTrip = linkedTrip;

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <LocalShipping color="action" />
              <Typography variant="h6">Trip Assignment</Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              {currentTripId ? (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Launch />}
                    onClick={() =>
                      openAppPopup(`/trips/${currentTripId}`, {
                        title: `Trip ${currentTripId}`,
                      })
                    }
                    sx={{ textTransform: "none" }}
                  >
                    View Trip
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    onClick={() => setModalOpen(true)}
                    sx={{ textTransform: "none" }}
                  >
                    Change Trip
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<LinkOff />}
                    disabled={removing}
                    onClick={handleRemove}
                    sx={{ textTransform: "none" }}
                  >
                    {removing ? "Removing…" : "Remove from Trip"}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setModalOpen(true)}
                    sx={{ textTransform: "none" }}
                  >
                    Assign to Trip
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OpenInNew />}
                    onClick={handleCreateTripPopup}
                    sx={{ textTransform: "none" }}
                  >
                    Create new trip
                  </Button>
                </>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {error && !modalOpen ? (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {error}
            </Typography>
          ) : null}

          {currentTripId ? (
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Trip ID
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {currentTripId}
                </Typography>
              </Box>
              {currentTrip?.routeCorridor || order?.routeCorridor ? (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Route Corridor
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCorridorDisplay(currentTrip?.routeCorridor || order.routeCorridor)}
                  </Typography>
                </Box>
              ) : null}
              {currentTrip?.departureDate ? (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Departure
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatDeparture(currentTrip.departureDate)}
                  </Typography>
                </Box>
              ) : null}
              {currentTrip?.status ? (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Trip Status
                  </Typography>
                  <Chip
                    size="small"
                    label={formatTripStatus(currentTrip.status)}
                    color={ACTIVE_TRIP_STATUS_SET.has(currentTrip.status) ? "info" : "default"}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              ) : null}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              This order is not assigned to a trip yet.
              {order?.routeCorridor
                ? ` Matching trips for ${orderRouteLabel} will be suggested.`
                : " Select an existing trip to schedule it for delivery."}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentTripId ? "Change trip assignment" : "Assign order to trip"}
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={2}
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              Select an existing trip to add order {orderId}. Completed trips are hidden.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNew />}
              onClick={handleCreateTripPopup}
              sx={{ textTransform: "none", flexShrink: 0 }}
            >
              Create new trip
            </Button>
          </Box>

          {order?.routeCorridor ? (
            <FormControlLabel
              control={
                <Switch
                  checked={matchRouteOnly}
                  onChange={(e) => setMatchRouteOnly(e.target.checked)}
                />
              }
              label={`Only show matching ${orderRouteLabel} trips`}
              sx={{ mb: 1 }}
            />
          ) : null}

          {error ? (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {error}
            </Typography>
          ) : null}

          <Divider sx={{ mb: 2 }} />

          {loadingTrips ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Trip</TableCell>
                    <TableCell>Route</TableCell>
                    <TableCell>Departure</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Orders</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleTrips.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          No eligible trips found.
                          {matchRouteOnly && order?.routeCorridor
                            ? " Try showing all corridors,"
                            : ""}{" "}
                          or create a new trip.
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Add />}
                          onClick={handleCreateTripPopup}
                          sx={{ mt: 1, textTransform: "none" }}
                        >
                          Create new trip
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleTrips.map((trip) => (
                      <TableRow key={trip.id} hover>
                        <TableCell>{trip.id}</TableCell>
                        <TableCell>{trip.routeCorridor || "—"}</TableCell>
                        <TableCell>{formatDeparture(trip.departureDate)}</TableCell>
                        <TableCell>{formatTripStatus(trip.status)}</TableCell>
                        <TableCell align="right">{trip.orders?.length || 0}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="contained"
                            disabled={assigningTripId === trip.id}
                            onClick={() => handleAssign(trip.id)}
                            sx={{ textTransform: "none" }}
                          >
                            {assigningTripId === trip.id ? "Assigning…" : "Assign"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderTripAssignmentCard;
